import { Sort2 as Sort } from '@innovyze/lib-am-common';
import {
	DataGrid,
	GridColDef,
	GridFilterItem,
	GridFilterModel,
	GridLogicOperator,
	useAdpAnalytics,
	useGridApiRef,
	useUnits,
} from '@innovyze/stylovyze';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-premium';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
	changeFiltersSelect,
	changePagination,
	changeSort,
	setRunCompleteReload,
	setTab,
} from '@Actions';
import { useGlobalization } from '@Translations/useGlobalization';

import { useDispatch, useSelector } from 'react-redux';
import Scales from '@Components/Utils/icons/Scales';
import { ExplanationPanel } from '../Explanation/ExplanationPanel.component';
import { DetailToggleCell } from './RenderCell/DetailToggleCell';
import { NoReasultsRisk } from './NoResultsRiskDatagrid.component';
import { TableToolbar } from './TableToolbar.component';
import { useRiskDetailsSelectors } from './useRiskDetailsSelectors';
import { FIELD_UNITS, useRiskDetailsColumns } from './TableColumns';
import { selectSelectionGridRows } from '@Selectors/Risk.selectors';
import { NamespacedStoreState } from '@Types/store.types';
import { useRiskDetailsStates } from './useRiskDetailsStates';
import useRiskDetailsRows from './useRiskDetailsRows';
import useRiskDetailsColumnOrder from './useRiskDetailsColumnOrder';
import { mapGridFilterItems } from '@Utils/gridFilterMapping';
import { convertFilterItemsToStoredUnits } from '@Utils/convertFilterUnits';
import { useResultsGridFlags } from './useResultsGridFlags';
import useGetRiskDetailsColumnVisibility from './useGetRiskDetailsColumnVisibility';
import { ReactMapProps } from '@innovyze/inno-map';
import { RiskDetailsRow } from '@Types/riskConfig.types';
import { useTrackRiskResultsView } from '@Hooks/useTrackRiskResultsView';

interface Props {
	configId: string;
	refreshRiskScore: () => void;
	setFocusedAssets?: (assets: ReactMapProps['focusedAssets']) => void;
	autoHeight?: boolean;
}

const validFilterValues: Record<string, string[]> = {
	CONDITION_DATA: ['0', '1'],
	GRADE: ['0', '1', '2', '3', '4'],
	CCTV_INSPECTED: ['true', 'false'],
	DATA_COVERAGE: ['complete', 'partial', 'minimal'],
};

// Redesign only: a "Show explanation" link in place of MUI's +/− toggle.
// Kept out of the persisted column order and always appended (pinned right).
export const DETAIL_TOGGLE_COLUMN: GridColDef = {
	field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
	headerName: '',
	align: 'right',
	sortable: false,
	filterable: false,
	hideable: false,
	disableColumnMenu: true,
	disableReorder: true,
	minWidth: 160,
	renderCell: params => <DetailToggleCell id={params.id} />,
};

const isEmptyFilterValue = (v: unknown) =>
	v === undefined ||
	v === null ||
	v === '' ||
	(Array.isArray(v) && v.length === 0);

export function isValidFilterItem(item: GridFilterItem): boolean {
	const allowed = validFilterValues[item.field];
	if (!allowed) return true; // validated by the attribute filter mapping
	// A half-chosen filter keeps its empty value in the panel (the dispatch
	// drops empties), so the controlled filterModel is unchanged.
	if (isEmptyFilterValue(item.value)) return true;
	const values: unknown[] = Array.isArray(item.value)
		? item.value
		: [item.value];
	return values.every(v => allowed.includes(v as string));
}

export function constructValidFilterModel(
	model: GridFilterModel,
): GridFilterModel {
	return {
		...model,
		items: model.items.map(item =>
			isValidFilterItem(item) ? item : { ...item, value: undefined },
		),
	};
}

export const RiskDetails = ({
	configId,
	refreshRiskScore,
	setFocusedAssets,
	autoHeight = true,
}: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const adp = useAdpAnalytics();

	const {
		tablePage,
		hasFullAccess,
		isLoading,
		isWaiting,
		riskConfig,
		canReload,
		runCompleteReload,
		riskDetailsPagination,
		isWaitingAssetLocation,
		isRiskStoreLoading,
		isSearching,
	} = useRiskDetailsSelectors();

	const apiRef = useGridApiRef();

	const {
		selectionModel,
		setSelectionModel,
		sortModel,
		setSortModel,
		filterModel,
		setFilterModel,
		paginationModel,
		setPaginationModel,
	} = useRiskDetailsStates();

	const selectedAssets = useSelector<NamespacedStoreState, RiskDetailsRow[]>(
		state => selectSelectionGridRows(state, selectionModel),
	);

	useEffect(() => {
		dispatch(setTab('details'));
	}, []);

	useEffect(() => {
		if (canReload && runCompleteReload) {
			refreshRiskScore();
			setRunCompleteReload(false);
		}
	}, [runCompleteReload]);

	useEffect(() => {
		setFocusedAssets?.(
			selectedAssets.map(asset => ({
				_id: `wwPipe#${asset.ASSET_ID}`,
				assetId: asset.ASSET_ID,
				assetType: configId,
				systemType: 'Risk',
				geometry: asset.ASSET_GEOMETRY,
			})),
		);
	}, [selectedAssets]);

	const rows = useRiskDetailsRows();

	useTrackRiskResultsView(adp, rows.length, riskConfig);

	const { isRedesign } = useResultsGridFlags();
	const { system, getStandardisedValue, getSystemUnit } = useUnits();
	const columns: GridColDef[] = useRiskDetailsColumns(riskConfig);
	const { columnOrder, setColumnOrder } = useRiskDetailsColumnOrder(
		columns.map(c => c.field),
		configId,
	);
	const orderedColumns = columnOrder
		.map(field => columns.find(c => c.field === field))
		.filter(Boolean) as GridColDef[];
	const gridColumns = isRedesign
		? orderedColumns.concat(DETAIL_TOGGLE_COLUMN)
		: orderedColumns;

	const dispatchSortFn = (name: string, desc: boolean) => {
		const sortPayload: Sort = {
			key: name,
			descending: desc,
		};
		setPaginationModel(pm => ({ ...pm, page: 0 }));
		dispatch(changeSort(sortPayload));
	};

	const { columnVisibilityModel, setColumnVisibilityModel } =
		useGetRiskDetailsColumnVisibility(configId);

	const filterRef = useMemo(() => React.createRef<HTMLButtonElement>(), []);

	// Per-asset Explanation (I360A-2744), fetched when a row is expanded.
	// Stable identities: MUI X rebuilds its per-row panel cache when they change.
	const getDetailPanelContent = useCallback(
		({ row }: { row: RiskDetailsRow }) => (
			<ExplanationPanel row={row} configId={configId} />
		),
		[configId],
	);
	const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

	return (
		<DataGrid
			apiRef={apiRef}
			rows={rows}
			sx={
				isRedesign
					? {
							'& .MuiDataGrid-columnHeaderTitle': {
								textTransform: 'none',
								color: '#1f2937',
								fontWeight: 600,
							},
							'& .MuiDataGrid-cell[data-field="CURRENT_RISK"]': {
								fontWeight: 600,
							},
							'& .MuiDataGrid-detailPanel': {
								backgroundColor: '#f8f9fb',
							},
					  }
					: undefined
			}
			onColumnOrderChange={co => {
				const field = co.column.field;
				const targetIndex = co.targetIndex;
				if (isRedesign) {
					// Splice the array the grid actually received, so MUI's
					// targetIndex lines up even if the stored order had extras.
					const fields = orderedColumns.map(c => c.field);
					const from = fields.indexOf(field);
					if (from === -1) return;
					fields.splice(from, 1);
					fields.splice(targetIndex, 0, field);
					setColumnOrder(fields);
					return;
				}
				setColumnOrder(prev => {
					const newOrder = [...prev];
					newOrder.splice(prev.indexOf(field), 1);
					newOrder.splice(targetIndex, 0, field);
					return newOrder;
				});
			}}
			columns={gridColumns}
			data-cy="risk-details-table"
			{...(isRedesign && { getDetailPanelContent, getDetailPanelHeight })}
			pinnedColumns={
				isRedesign
					? {
							left: ['ASSET_ID'],
							right: ['Menu', GRID_DETAIL_PANEL_TOGGLE_FIELD],
					  }
					: { left: ['ASSET_ID'], right: ['GRADE', 'Menu'] }
			}
			className={
				isWaiting || isWaitingAssetLocation ? 'wait' : 'ScrollTable'
			}
			emptyState={{
				title: 'No results to show',
				subtitle: (
					<NoReasultsRisk
						adminFullAccess={hasFullAccess}
						configId={configId}
					/>
				),
				icon: <Scales />,
				color: '#4A6067',
			}}
			loading={
				isLoading || isWaiting || isRiskStoreLoading || isSearching
			}
			autoHeight={autoHeight}
			skeletonRowCount={10}
			pageSizeOptions={[5, 10, 15, 25, 50, 100]}
			paginationMode="server"
			paginationModel={paginationModel}
			onPaginationModelChange={_paginationModel => {
				if (paginationModel.pageSize != _paginationModel.pageSize) {
					_paginationModel.page = 0;
				}
				setPaginationModel(_paginationModel);
				dispatch(
					changePagination({
						page: _paginationModel.page + 1,
						slice: _paginationModel.pageSize,
					}),
				);
			}}
			columnVisibilityModel={columnVisibilityModel}
			onColumnVisibilityModelChange={setColumnVisibilityModel}
			footerHelper={
				isLoading
					? ''
					: t('{{from}}-{{to}} of {{total}}', {
							from:
								(riskDetailsPagination.page - 1) *
									riskDetailsPagination.slice +
								1,
							to:
								riskDetailsPagination.page ==
								riskDetailsPagination.pages
									? riskDetailsPagination.total
									: riskDetailsPagination.page *
									  riskDetailsPagination.slice,
							total: riskDetailsPagination.total,
					  })
			}
			slots={{
				toolbar: TableToolbar,
			}}
			slotProps={{
				panel: {
					// anchorEl: filterRef.current, // Bug 155409: MUI data grids filter anchoring
				},
				toolbar: {
					disable: isLoading || isWaiting || isWaitingAssetLocation,
					hasFullAccess: hasFullAccess,
					showFilterPanel: apiRef.current.showFilterPanel,
					refreshRiskScore: refreshRiskScore,
					filterRef: filterRef,
					lastRunCompleted: riskConfig.riskSetting.lastRunCompleted,
				},
				filterPanel: {
					logicOperators: [GridLogicOperator.And],
				},
			}}
			rowCount={tablePage.total}
			pagination
			onRowSelectionModelChange={setSelectionModel}
			disableMultipleColumnsSorting
			onSortModelChange={sm => {
				if (!sm.length) {
					if (!sortModel.length) {
						return sm.push({ field: 'CURRENT_RISK', sort: 'desc' });
					} else {
						const sortedField = sortModel?.[0];
						sortedField.sort =
							sortedField.sort === 'asc' ? 'desc' : 'asc';
						sm.push(sortModel[0]);
					}
				}
				sm.forEach(sort => {
					const key = sort?.field;
					const descending = sort?.sort !== 'asc';
					dispatchSortFn(key, descending);
				});
				if (!sm.length) dispatchSortFn('CURRENT_RISK', true);
				setSortModel(sm);
			}}
			sortingMode="server"
			sortingOrder={['desc', 'asc']}
			sortModel={sortModel}
			filterToolbar
			filterModel={filterModel}
			filterMode="server"
			onFilterModelChange={(model: GridFilterModel) => {
				const tmpModel = constructValidFilterModel(model);
				setFilterModel(tmpModel);

				const filterValueModel = isRedesign
					? mapGridFilterItems(
							convertFilterItemsToStoredUnits(
								tmpModel.items,
								FIELD_UNITS,
								{
									system,
									getSystemUnit,
									getStandardisedValue,
								},
							),
					  )
					: tmpModel.items
							.map(item => ({
								colKey: item.field,
								value: item.value,
							}))
							.filter(item => item.value && item.colKey);

				dispatch(changeFiltersSelect(filterValueModel));
			}}
		/>
	);
};
